type Checkboxes = Record<string, boolean>;

// The rules themselves live in PermissionRegistry::DEPENDENCIES and arrive as props. This module
// only interprets them, so the graph is defined in exactly one place.
//
// The array is ordered and that order matters: the vouchers rule must resolve before the deals
// rule, otherwise granting elite admin leaves deals forced on by a voucher grant that the same
// pass is about to clear.
export interface DependencyRule {
  attr: string;
  // Locked off unless every listed permission is granted.
  requires?: string[];
  // Locked off while any listed permission is granted, because that permission supersedes this one.
  lockedBy?: string[];
  // Forced on (and locked) while any listed permission is granted.
  forcedOnBy?: string[];
}

// office_admin is not one of the permission checkboxes — the tab tracks it separately.
const OFFICE_ADMIN = 'office_admin';

function granted(attr: string, checkboxes: Checkboxes, officeAdminChecked: boolean): boolean {
  return attr === OFFICE_ADMIN ? officeAdminChecked : !!checkboxes[attr];
}

function missingRequirement(rule: DependencyRule, checkboxes: Checkboxes, officeAdminChecked: boolean): boolean {
  return (rule.requires ?? []).some(attr => !granted(attr, checkboxes, officeAdminChecked));
}

function superseded(rule: DependencyRule, checkboxes: Checkboxes, officeAdminChecked: boolean): boolean {
  return (rule.lockedBy ?? []).some(attr => granted(attr, checkboxes, officeAdminChecked));
}

function forcedOn(rule: DependencyRule, checkboxes: Checkboxes, officeAdminChecked: boolean): boolean {
  return (rule.forcedOnBy ?? []).some(attr => granted(attr, checkboxes, officeAdminChecked));
}

export function dependencyDisabled(
  rules: DependencyRule[],
  attr: string,
  checkboxes: Checkboxes,
  officeAdminChecked: boolean,
): boolean {
  const rule = rules.find(candidate => candidate.attr === attr);
  if (!rule) return false;

  return missingRequirement(rule, checkboxes, officeAdminChecked)
    || superseded(rule, checkboxes, officeAdminChecked)
    || forcedOn(rule, checkboxes, officeAdminChecked);
}

export function applyDependencies(
  rules: DependencyRule[],
  checkboxes: Checkboxes,
  officeAdminChecked: boolean,
  previous?: Checkboxes,
): Checkboxes {
  const next = { ...checkboxes };

  rules.forEach((rule) => {
    if (!(rule.attr in next)) return;

    if (forcedOn(rule, next, officeAdminChecked)) {
      next[rule.attr] = true;
    } else if (missingRequirement(rule, next, officeAdminChecked) || superseded(rule, next, officeAdminChecked)) {
      next[rule.attr] = false;
    }
  });

  // Releasing a permission also releases whatever it had forced on. This is a transition rather
  // than a steady state, so it needs the prior values to tell "just revoked" from "already off
  // on load".
  rules.forEach((rule) => {
    if (!(rule.attr in next)) return;

    const released = (rule.forcedOnBy ?? []).some(
      source => previous?.[source] && !granted(source, next, officeAdminChecked),
    );

    if (released) next[rule.attr] = false;
  });

  return next;
}
